/**
 * useActivityLogs
 *
 * Fetches activity_logs from the database for the admin Activity tab.
 * Supports optional server-side date range filtering for performance.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ActivityLog {
  id: string;
  event_type: string;
  actor_id: string | null;
  actor_email: string | null;
  entity_type: string;
  entity_id: string;
  metadata: Record<string, any>;
  created_at: string;
}

export interface ActivityLogsDateRange {
  from: Date;
  to: Date;
}

export function useActivityLogs(eventTypeFilter?: string, dateRange?: ActivityLogsDateRange) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("activity_logs")
      .select("*")
      .order("created_at", { ascending: false });

    if (eventTypeFilter && eventTypeFilter !== "all") {
      query = query.eq("event_type", eventTypeFilter);
    }

    if (dateRange) {
      query = query
        .gte("created_at", dateRange.from.toISOString())
        .lte("created_at", dateRange.to.toISOString());
    }

    // When no date range, keep the 200-record limit as a safety guard.
    // With a date range, allow full results within the range.
    if (!dateRange) {
      query = query.limit(200);
    } else {
      query = query.limit(1000);
    }

    const { data, error } = await query;
    if (!error && data) {
      setLogs(data as ActivityLog[]);
    }
    setLoading(false);
  }, [eventTypeFilter, dateRange?.from?.getTime(), dateRange?.to?.getTime()]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return { logs, loading, refetch: fetchLogs };
}
